import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  getAllProductCategories,
  getAllValidProducts
} from "../../../api/action/products.jsx";
import {
  getEducationPageCacheKey,
  readEducationPageCache,
  writeEducationPageCache,
} from "../../../domains/education/utils/educationPageCache";
import { KRUMAGO } from "../../s3-assets/Krumago.jsx";
import FeaturedCourses from "./FeaturedCourses.jsx";
import TestimonySection from "./components/TestimonySection.jsx";
import RichTextDisplay from "../../../components/RichTextDisplay.jsx";

const itemIdentity = (item = {}) => item.primaryId || item._id || item.id || item.code || item.name;

const itemSnapshot = (item) => {
  try {
    return JSON.stringify(item);
  } catch {
    return "";
  }
};

const reconcileList = (currentItems = [], nextItems = []) => {
  const currentById = new Map();
  currentItems.forEach((item) => {
    const id = itemIdentity(item);
    if (id != null) currentById.set(String(id), item);
  });

  let changed = currentItems.length !== nextItems.length;
  const reconciled = nextItems.map((item, index) => {
    const id = itemIdentity(item);
    const current = id != null ? currentById.get(String(id)) : null;
    if (current && itemSnapshot(current) === itemSnapshot(item)) {
      if (currentItems[index] !== current) changed = true;
      return current;
    }
    if (currentItems[index] !== item) changed = true;
    return item;
  });

  return changed ? reconciled : currentItems;
};

const EducationDomainTemplate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoggedIn = useSelector((state) => state.auth?.isAuthenticated) === true;

  const companyState = useSelector((state) => state.companyReducer.company);
  const companyDetails = companyState && typeof companyState === 'object' ? companyState : {};
  const companyPrimayId = companyState?.primaryId;
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [category, setCategory] = useState([]);
  const setStableCategoryProducts = useCallback((nextProducts = []) => {
    setCategoryProducts((currentProducts) => reconcileList(currentProducts, nextProducts));
  }, []);
  const setStableCategory = useCallback((nextCategories = []) => {
    setCategory((currentCategories) => reconcileList(currentCategories, nextCategories));
  }, []);

  const handleSignInClick = () => {
    navigate("/sign-in");
  };

  useEffect(() => {
    let isCurrent = true;

    if (!companyPrimayId) {
      setStableCategory([]);
      setStableCategoryProducts([]);
      return () => {
        isCurrent = false;
      };
    }

    const cacheKey = getEducationPageCacheKey('home', { companyId: companyPrimayId });
    const cached = readEducationPageCache(cacheKey);
    if (cached) {
      setStableCategory(cached.category || []);
      setStableCategoryProducts(cached.categoryProducts || []);
    }

    const nextCache = {
      category: cached?.category || [],
      categoryProducts: cached?.categoryProducts || [],
    };

    getAllProductCategories(companyPrimayId)(dispatch)
      .then((categories) => {
        if (!isCurrent) return;
        nextCache.category = categories || [];
        setStableCategory(nextCache.category);
        writeEducationPageCache(cacheKey, nextCache);
      })
      .catch(() => {
        if (isCurrent && !cached) setStableCategory([]);
      });

    getAllValidProducts(companyPrimayId)(dispatch)
      .then((products) => {
        if (!isCurrent) return;
        nextCache.categoryProducts = products || [];
        setStableCategoryProducts(nextCache.categoryProducts);
        writeEducationPageCache(cacheKey, nextCache);
      })
      .catch(() => {
        if (isCurrent && !cached) setStableCategoryProducts([]);
      });

    return () => {
      isCurrent = false;
    };
  }, [companyPrimayId, dispatch, setStableCategory, setStableCategoryProducts]);

  const CategorySection = ({ categories }) => {
    const navigate = useNavigate();

    const handleCategoryClick = (categoryId) => {
      navigate(`/course?category=${categoryId}`);
    };

    return (
      <section
        style={{
          padding: "4rem 2rem",
          backgroundColor: "#ffffff",
        }}
      >
        <div
          style={{
            maxWidth: "1600px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "4rem",
            alignItems: "center", // Changed to center for vertical alignment
          }}
        >
          {/* Header - Centered vertically with cards */}
          <div style={{ textAlign: "left" }}>
            <h2
              style={{
                fontSize: "2.25rem",
                fontWeight: "700",
                color: "#1a202c",
                marginBottom: "1.5rem",
                lineHeight: "1.2",
              }}
            >
              Learn essential career and life skills
            </h2>
            <p
              style={{
                fontSize: "1.125rem",
                color: "#4a5568",
                lineHeight: "1.6",
                margin: 0,
              }}
            >
              AVPS academy helps you build in-demand skills fast and advance your career in a changing job market.
            </p>
          </div>

          {/* Categories Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "2rem",
              alignItems: "stretch", // Ensures all cards have same height
            }}
          >
            {categories.slice(0, 3).map((category, index) => {
              // Use displayLink from imageContent object if it exists
              const imageUrl = category?.imageContent?.displayLink ||
                category?.imageURL ||
                category?.imageContent?.storedLink ||
                '';

              return (
                <div
                  key={category.id || index}
                  onClick={() => handleCategoryClick(category?.primaryId)}
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    padding: "0",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: "1px solid #e2e8f0",
                    textAlign: "left",
                    position: "relative",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                    overflow: "hidden",
                    minHeight: "320px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.borderColor = "var(--education-secondary, #3B9394)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                  }}
                >
                  {/* Category Image */}
                  {imageUrl && (
                    <div
                      style={{
                        height: "140px",
                        position: "relative",
                        flexShrink: 0,
                        overflow: "hidden",
                        backgroundColor: "#f1f5f9",
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt={category.name || "Course category"}
                        loading={index === 0 ? "eager" : "lazy"}
                        fetchpriority={index === 0 ? "high" : "auto"}
                        decoding="async"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                          display: "block",
                        }}
                      />
                    </div>
                  )}

                  {/* Content Area */}
                  <div
                    style={{
                      padding: "1.5rem",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Category Name */}
                    <h3
                      style={{
                        fontSize: "1.25rem",
                        fontWeight: "600",
                        color: "#1a202c",
                        margin: "0 0 0.5rem 0",
                        lineHeight: "1.3",
                      }}
                    >
                      {category.name}
                    </h3>

                    {/* Description */}
                    <div style={{ flex: 1, marginBottom: "1rem" }}>
                      <RichTextDisplay
                        content={category.description || "Master essential skills with expert-led courses"}
                        sx={{
                          fontSize: "0.9rem",
                          color: "#4a5568",
                          lineHeight: "1.5",
                        }}
                      />
                    </div>

                    {/* CTA Button */}
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "var(--education-secondary, #3B9394)",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        padding: "0.5rem 0",
                        marginTop: "auto",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateX(4px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateX(0)";
                      }}
                    >
                      Explore Courses
                      <span style={{ marginLeft: "0.5rem", transition: "transform 0.2s ease" }}>
                        →
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>


      </section>
    );
  };

  return (
    <>
      <section
        className="relative py-20 px-8 bg-cover bg-center min-h-screen flex items-center"
        style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.7)), url(${KRUMAGO.IMAGES.LANDING_PAGE})` }}
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left Content Section */}
            <div className="text-white">


              <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Transform Your Career with
                <span className="education-primary-text"> Expert-Led</span> Courses
              </h1>

              <p className="text-xl text-gray-200 leading-relaxed mb-8">
                Join thousands of professionals who have accelerated their careers with our industry-relevant curriculum, hands-on projects, and personalized mentorship.
              </p>

              {/* Key Features */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 education-secondary-bg rounded-full flex items-center justify-center">
                    <span className="text-white text-sm"> </span>
                  </div>
                  <span className="text-gray-200">Industry Experts</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 education-secondary-bg rounded-full flex items-center justify-center">
                    <span className="text-white text-sm"> </span>
                  </div>
                  <span className="text-gray-200">Flexible Schedule</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 education-secondary-bg rounded-full flex items-center justify-center">
                    <span className="text-white text-sm"> </span>
                  </div>
                  <span className="text-gray-200">Hands-on Projects</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 education-secondary-bg rounded-full flex items-center justify-center">
                    <span className="text-white text-sm"> </span>
                  </div>
                  <span className="text-gray-200">Career Support</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  id="explore-courses-btn"
                  onClick={() => navigate('/course')}
                  className="education-primary-button text-white font-semibold py-4 px-8 rounded-lg transition duration-300 transform hover:scale-105"
                >
                  Explore All Courses
                </button>
                <button
                  id="signup-button"
                  onClick={() => navigate('/sign-up')}
                  className="border-2 education-outline-secondary-button font-semibold py-4 px-8 rounded-lg transition duration-300"
                >
                  Sign Up Now
                </button>
              </div>


              {/* Stats */}
              <div className="flex space-x-8 text-center">
                <div className="text-center">
                  <div className="text-2xl font-bold education-primary-text">100%</div>
                  <div className="text-gray-300 text-sm">Master your skill</div>
                </div>

              </div>
            </div>

            {/* Right Content - Course Details Card */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 transform hover:scale-105 transition duration-300">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Course Highlights
              </h2>

              {/* Curriculum Details */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="education-primary-soft-bg p-3 rounded-lg">
                    <span className="education-primary-text text-lg">📚</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Comprehensive Curriculum</h3>
                    <p className="text-gray-600 text-sm">Industry-aligned courses with latest technologies and frameworks</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="education-secondary-soft-bg p-3 rounded-lg">
                    <span className="education-secondary-text text-lg">⏰</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Flexible Schedule</h3>
                    <p className="text-gray-600 text-sm">Learn at your own pace with 24/7 access to course materials</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="education-primary-soft-bg p-3 rounded-lg">
                    <span className="education-primary-text text-lg">👨‍🏫</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">1:1 Mentorship</h3>
                    <p className="text-gray-600 text-sm">Personalized guidance from industry experts</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="education-secondary-soft-bg p-3 rounded-lg">
                    <span className="education-secondary-text text-lg">💼</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Career Services</h3>
                    <p className="text-gray-600 text-sm">Resume building, interview prep, and job placement support</p>
                  </div>
                </div>
              </div>

              {/* Schedule Customization */}
              <div className="mt-8 p-6 bg-gray-50 rounded-xl">

                <button onClick={() => navigate('/course')} className="w-full education-gradient-bg text-white py-3 rounded-lg font-semibold mt-4 transition duration-300 transform hover:scale-105">
                  Start Learning Journey
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CategorySection categories={category} />

      <section className="py-20 px-4 bg-white relative">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">

            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Why <span className="education-primary-text font-extrabold">Choose</span> Our Academy
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how our comprehensive approach to education transforms careers and
              bridges the gap between learning and real-world success.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Feature 1 */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 education-card-hover-secondary hover:transform hover:-translate-y-2">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-2xl">🎓</span>
                  </div>

                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 education-group-primary-text transition-colors duration-300">
                    Bridging the Knowledge Gap
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    The IT industry evolves rapidly with new technologies and trends. We're committed to
                    bridging this gap by equipping you with cutting-edge skills needed to thrive in today's
                    competitive landscape.
                  </p>
                  <Link
                    to="/BridgingTheKnowledgeGap"
                    className="inline-flex items-center education-secondary-text font-semibold education-hover-primary-text transition-colors duration-300 group-hover:translate-x-1"
                  >
                    Learn More
                    <svg className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 education-card-hover-primary hover:transform hover:-translate-y-2">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-2xl text-white">💡</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 education-group-secondary-text transition-colors duration-300">
                    Insights & Effective Engagement
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    We believe learning thrives through collaboration. Our exceptional IT training is
                    built on fostering meaningful interactions, personalized feedback, and engaging
                    learning experiences.
                  </p>
                  <Link
                    to="/InsightsAndEngagement"
                    className="inline-flex items-center education-secondary-text font-semibold education-hover-primary-text transition-colors duration-300 group-hover:translate-x-1"
                  >
                    Learn More
                    <svg className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 education-card-hover-secondary hover:transform hover:-translate-y-2">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-2xl text-white">🏗️</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 education-group-primary-text transition-colors duration-300">
                    Build Industry Experience
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Our "Build Industry Experience" program seamlessly bridges theoretical knowledge with
                    practical application, providing you with real-world skills demanded by leading IT
                    companies and startups.
                  </p>
                  <Link
                    to="/BuildIndustryExperience"
                    className="inline-flex items-center education-secondary-text font-semibold education-hover-primary-text transition-colors duration-300 group-hover:translate-x-1"
                  >
                    Learn More
                    <svg className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 education-card-hover-primary hover:transform hover:-translate-y-2">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-2xl text-white">💼</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 education-group-secondary-text transition-colors duration-300">
                    Technical & Soft Skills Development
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    We equip you with in-demand technical expertise while simultaneously honing essential
                    soft skills, creating well-rounded professionals ready to excel in any industry environment.
                  </p>
                  <Link
                    to="/GroomingSkills"
                    className="inline-flex items-center education-secondary-text font-semibold education-hover-primary-text transition-colors duration-300 group-hover:translate-x-1"
                  >
                    Learn More
                    <svg className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}


        </div>
      </section>

      {/* Second section */}
      {categoryProducts && categoryProducts.length > 0 && (
        <section
          style={{
            backgroundColor: "#ffffff",
            padding: "4rem 0",
            textAlign: "center",
          }}
        >

          <FeaturedCourses
            productList={categoryProducts.slice(0, 4)}
            navigate={navigate}
            loggedIn={isLoggedIn}
          />
        </section>
      )}

      <TestimonySection
        companyName={companyState?.name || "AVPS Academy"}
        companyPrimaryId={companyState?.primaryId}
      />

      {/* Fifth section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Background with Overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${KRUMAGO.IMAGES.LANDING_PAGE})`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/95 to-gray-900/90"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center space-x-2 mb-4">
              <div className="w-3 h-3 education-primary-bg rounded-full animate-pulse"></div>
              <div className="w-3 h-3 education-secondary-bg rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-3 h-3 education-primary-bg rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our <span className="education-primary-text">Mission</span> & Vision
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Driving innovation in education to create meaningful impact and transform lives through learning
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Left Content - Mission Statement */}
            <div className="text-white">
              {/* Mission Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 education-card-hover-secondary transition-all duration-300">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 education-gradient-br rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl">🎯</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Our Mission</h3>
                    <p className="text-gray-200 text-lg leading-relaxed">
                      {companyDetails.description || "To democratize quality education and empower individuals with the skills needed to thrive in the digital economy through innovative learning solutions and personalized mentorship."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Vision Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 education-card-hover-primary transition-all duration-300 mt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 education-gradient-br-reverse rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl"></span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-2">Our Vision</h3>
                    <p className="text-gray-200 text-lg leading-relaxed">
                      Creating a world where anyone, anywhere can transform their life through accessible, high-quality education that bridges the gap between learning and real-world success.
                    </p>
                  </div>
                </div>
              </div>

              {/* Founder Signature */}
              <div className="mt-8 p-6 education-gradient-bg rounded-2xl text-center">
                <p className="text-white italic text-lg mb-4">
                  "Education is the passport to the future, for tomorrow belongs to those who prepare for it today."
                </p>
                <div>
                  <h3 className="font-bold text-white text-xl">{companyDetails.OwenerName || "Founder & CEO"}</h3>
                  <p className="text-gray-200 text-sm">{companyDetails.name || "AVPS Academy"}</p>
                </div>
              </div>
            </div>

            {/* Right Content - Impact & Values */}
            <div className="space-y-6">
              {/* Impact Stats */}

              <br />< br /><br />< br /><br />< br />
              {/* Core Values */}
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Our Core Values</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl education-hover-secondary-bg hover:text-white transition-all duration-300 group">
                    <div className="w-10 h-10 education-secondary-soft-bg rounded-lg flex items-center justify-center group-hover:bg-white">
                      <span className="education-secondary-text text-lg education-group-secondary-text">💡</span>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800 group-hover:text-white">Innovation</h5>
                      <p className="text-gray-600 text-sm group-hover:text-gray-200">Embracing cutting-edge teaching methodologies</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl education-hover-primary-bg hover:text-white transition-all duration-300 group">
                    <div className="w-10 h-10 education-primary-soft-bg rounded-lg flex items-center justify-center group-hover:bg-white">
                      <span className="education-primary-text text-lg education-group-primary-text">🤝</span>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800 group-hover:text-white">Collaboration</h5>
                      <p className="text-gray-600 text-sm group-hover:text-gray-200">Building learning communities that support growth</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl education-hover-secondary-bg hover:text-white transition-all duration-300 group">
                    <div className="w-10 h-10 education-secondary-soft-bg rounded-lg flex items-center justify-center group-hover:bg-white">
                      <span className="education-secondary-text text-lg education-group-secondary-text">⭐</span>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800 group-hover:text-white">Excellence</h5>
                      <p className="text-gray-600 text-sm group-hover:text-gray-200">Delivering world-class educational experiences</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <div className="text-center">
                <button
                  onClick={() => navigate('/about')}
                  className="w-full education-gradient-bg text-white font-semibold py-4 px-8 rounded-xl transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  Learn More About Our Journey
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Border */}
        <div className="absolute bottom-0 left-0 right-0 h-1 education-secondary-line-gradient opacity-50"></div>
      </section>
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left Content - Image */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-200">
                <img
                  loading="lazy"
                  src={KRUMAGO.IMAGES.WOMEN_IMG}
                  alt="Professional joining our learning community"
                  className="w-full h-auto rounded-xl shadow-md"
                />
              </div>

              {/* Floating Stats */}


              <div className="absolute -bottom-4 -left-4 education-secondary-bg text-white p-4 rounded-xl shadow-lg">
                <div className="text-center">
                  <div className="text-sm opacity-90">Your Success Our Goal</div>
                </div>
              </div>
            </div>

            {/* Right Content - Join Us */}
            <div className="space-y-6">
              {/* Header */}


              <h2 className="text-4xl md:text-5xl font-bold text-gray-800">
                Join Our <span className="education-primary-text">Learning</span> Community
              </h2>

              <p className="text-xl text-gray-600 leading-relaxed">
                Connect with like-minded professionals, enhance your skills with industry-relevant courses,
                and accelerate your career growth in a supportive learning environment.
              </p>

              {/* Features List */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-green-600 text-sm"> </span>
                  </div>
                  <span className="text-gray-700">Expert-Led Courses</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600 text-sm"> </span>
                  </div>
                  <span className="text-gray-700">Career Support</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-purple-600 text-sm"> </span>
                  </div>
                  <span className="text-gray-700">Flexible Learning</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-orange-600 text-sm"> </span>
                  </div>
                  <span className="text-gray-700">Networking Opportunities</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleSignInClick}
                  className="education-gradient-bg text-white font-semibold py-4 px-8 rounded-xl transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <span>Start Learning Journey</span>
                  <span className="text-lg">→</span>
                </button>

                <button
                  onClick={() => navigate('/course')}
                  className="border-2 education-outline-secondary-button font-semibold py-4 px-8 rounded-xl transition duration-300 flex items-center justify-center gap-2"
                >
                  <span>Explore Courses</span>
                  <span className="text-lg">📚</span>
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 pt-6 border-t border-gray-200">

                <div className="text-center">
                  <div className="text-2xl font-bold education-secondary-text">100%</div>
                  <div className="text-gray-600 text-sm">Satisfaction</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold education-primary-text">1:1</div>
                  <div className="text-gray-600 text-sm">Mentorship</div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Testimonials */}

        </div>
      </section>
    </>
  );
};

export default EducationDomainTemplate;
